import { Injectable } from '@angular/core';

/**
 * Caixa onde um campo é escrito, em coordenadas RELATIVAS à imagem (0..1).
 * Guardar em percentual (e não em pixels) faz o mesmo modelo servir para
 * qualquer resolução de exportação da mesma arte.
 */
export interface CaixaArte {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Um texto a ser escrito sobre a arte, cobrindo o placeholder original. */
export interface CampoArte {
  /** Rótulo exibido na tela de calibração ("Nome", "Cupom", "Vencimento"). */
  rotulo: string;
  /** Template com placeholders, ex.: '{NOME}', '{NOME:20}', '{VCTO}'. */
  template: string;
  /** Só existe depois que o operador marcou o lugar na tela de calibração. */
  caixa?: CaixaArte;
  /** Cor do texto novo; amostrada da própria arte na calibração. */
  corTexto: string;
  /** Cor usada para apagar o placeholder original (fundo chapado). */
  corFundo: string;
  align: 'left' | 'center' | 'right';
  fontFamily: string;
  peso: string;
  maiusculas: boolean;
}

/** Dados que alimentam os placeholders de UM destinatário. */
export interface ContextoArte {
  /** Nome como está no cadastro (pode vir em CAIXA ALTA). */
  nome: string;
  /** Percentual de desconto da campanha, ex.: 20. */
  desconto: number;
  /** Data-base do disparo — congelada no início do envio. */
  dataBase: Date;
  /** Prazo padrão do cupom, em dias úteis. */
  prazoDiasUteis: number;
}

/** Configuração da arte inteira, salva/reaproveitada entre campanhas. */
export interface ModeloArte {
  campos: CampoArte[];
  desconto: number;
  prazoDiasUteis: number;
}

const DEFAULT_FONT = 'Poppins, Montserrat, "Arial Black", Arial, sans-serif';

@Injectable({ providedIn: 'root' })
export class ArtePersonalizadaService {

  /**
   * Campos que a arte de aniversário usa, sem caixa — a calibração define o lugar.
   * Nome e cupom saem em caixa alta porque é assim que os placeholders aparecem
   * desenhados na arte; a data fica como está.
   */
  camposPadrao(): CampoArte[] {
    return [
      this.novoCampo('Nome', '{NOME}', true),
      this.novoCampo('Cupom', '{NOME:20}', true),
      this.novoCampo('Vencimento', '{VCTO}')
    ];
  }

  novoCampo(rotulo: string, template: string, maiusculas = false): CampoArte {
    return {
      rotulo,
      template,
      caixa: undefined,
      corTexto: '#FFFFFF',
      corFundo: '#000000',
      align: 'center',
      fontFamily: DEFAULT_FONT,
      peso: '800',
      maiusculas
    };
  }

  // ---------------------------------------------------------------- texto

  /**
   * Troca os placeholders do template pelos valores do destinatário.
   *
   * O `nome` do contexto é o nome fantasia do cadastro (com a razão social como
   * reserva, quando o fantasia está em branco).
   *
   * Placeholders reconhecidos (o `:arg` é opcional e sobrepõe o padrão da campanha):
   *   {NOME}            -> fantasia inteiro, capitalizado ..... "Ana Maria De Souza"
   *   {NOME:20}         -> código do cupom .................... "ANA20"
   *   {PRIMEIRO_NOME}   -> só o primeiro nome ................. "Ana"
   *   {CUPOM} {CUPOM:25}-> idem {NOME:xx}, usando o desconto da campanha
   *   {DESCONTO}        -> percentual da campanha ............. "20%"
   *   {VCTO} {VCTO:30}  -> data-base + N dias úteis ........... "05/09/2026"
   *
   * Um placeholder desconhecido é mantido como está, de propósito: assim o erro
   * de digitação aparece no preview em vez de sumir silenciosamente da arte.
   */
  resolver(template: string, ctx: ContextoArte): string {
    if (!template) {
      return '';
    }
    return template.replace(
      /\{\s*([A-Za-z_]+)\s*(?::\s*([^}]*?))?\s*\}/g,
      (original: string, chave: string, arg: string): string => {
        const nome = ctx.nome || '';
        switch (chave.toUpperCase()) {
          case 'NOME':
            // Com argumento vira código de cupom (curto, só o primeiro nome);
            // sem argumento, o fantasia inteiro.
            return arg !== undefined && arg !== null && arg !== ''
              ? this.codigoCupom(nome, arg)
              : this.capitalizar(nome);
          case 'PRIMEIRO_NOME':
            return this.capitalizar(this.primeiroNome(nome));
          case 'NOME_COMPLETO':
            return this.capitalizar(nome);
          case 'CUPOM':
            return this.codigoCupom(nome, arg || String(ctx.desconto));
          case 'DESCONTO':
            return `${arg || ctx.desconto}%`;
          case 'VCTO':
          case 'VENCIMENTO': {
            const prazo = Number(arg) > 0 ? Number(arg) : ctx.prazoDiasUteis;
            return this.formatarData(this.somarDiasUteis(ctx.dataBase, prazo));
          }
          default:
            return original;
        }
      }
    );
  }

  /** Primeiro nome — o que cabe na arte e soa melhor que a razão social inteira. */
  primeiroNome(nome: string): string {
    const limpo = (nome || '').trim().replace(/\s+/g, ' ');
    if (!limpo) {
      return '';
    }
    return limpo.split(' ')[0];
  }

  /** "MARIA DA SILVA" -> "Maria Da Silva" (o cadastro costuma vir em caixa alta). */
  capitalizar(nome: string): string {
    return (nome || '')
      .trim()
      .replace(/\s+/g, ' ')
      .toLowerCase()
      .replace(/(^|\s|')([a-zà-ÿ])/g, (_m, sep, letra) => sep + letra.toUpperCase());
  }

  /** "José" + "20" -> "JOSE20" (sem acento, sem espaço, tamanho limitado). */
  codigoCupom(nome: string, sufixo: string): string {
    const base = this.primeiroNome(nome)
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^A-Za-z0-9]/g, '')
      .toUpperCase()
      .substring(0, 12);
    return `${base}${(sufixo || '').trim()}`;
  }

  /**
   * Soma dias ÚTEIS pulando sábado e domingo.
   * Feriado não entra: não existe cadastro de feriados no sistema. Para um cupom
   * promocional a diferença de um dia é irrelevante — e quando houver esse
   * cadastro, basta trocar este método.
   */
  somarDiasUteis(base: Date, dias: number): Date {
    const data = new Date(base.getFullYear(), base.getMonth(), base.getDate());
    let restantes = Math.max(0, Math.floor(dias || 0));
    while (restantes > 0) {
      data.setDate(data.getDate() + 1);
      const dow = data.getDay();
      if (dow !== 0 && dow !== 6) {
        restantes--;
      }
    }
    return data;
  }

  formatarData(data: Date): string {
    const dd = String(data.getDate()).padStart(2, '0');
    const mm = String(data.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${data.getFullYear()}`;
  }

  // ------------------------------------------------------------- imagem

  /** Carrega o anexo uma única vez; o mesmo elemento é reusado em todos os renders. */
  carregarImagem(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = (): void => {
        // Já decodificada: pode liberar a URL sem perder o bitmap.
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = (): void => {
        URL.revokeObjectURL(url);
        reject(new Error('Não foi possível ler a imagem anexada.'));
      };
      img.src = url;
    });
  }

  ehImagem(file: File): boolean {
    return !!file && (file.type || '').startsWith('image/');
  }

  /**
   * Desenha os campos sobre a arte e devolve o PNG resultante.
   * Cada campo primeiro cobre o placeholder original com a cor de fundo e só
   * então escreve o texto — por isso só funciona bem sobre fundo chapado.
   */
  renderizar(img: HTMLImageElement, campos: CampoArte[], ctx: ContextoArte): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;

    const g = this.contexto2d(canvas);
    g.drawImage(img, 0, 0, canvas.width, canvas.height);

    (campos || []).filter(c => !!c.caixa).forEach(campo => {
      const texto = this.resolver(campo.template, ctx);
      const x = campo.caixa.x * canvas.width;
      const y = campo.caixa.y * canvas.height;
      const w = campo.caixa.w * canvas.width;
      const h = campo.caixa.h * canvas.height;

      // 1) apaga o placeholder original
      g.fillStyle = campo.corFundo;
      g.fillRect(x, y, w, h);

      if (!texto) {
        return;
      }

      // 2) escreve o texto novo, encolhendo até caber na caixa
      const conteudo = campo.maiusculas ? texto.toUpperCase() : texto;
      const fonte = (tam: number): string => `${campo.peso} ${tam}px ${campo.fontFamily}`;

      let tamanho = Math.max(8, Math.floor(h * 0.78));
      g.font = fonte(tamanho);
      const largura = g.measureText(conteudo).width;
      if (largura > w && largura > 0) {
        // estimativa proporcional primeiro, depois ajuste fino
        tamanho = Math.max(8, Math.floor(tamanho * (w / largura)));
        g.font = fonte(tamanho);
        while (tamanho > 8 && g.measureText(conteudo).width > w) {
          tamanho--;
          g.font = fonte(tamanho);
        }
      }

      g.fillStyle = campo.corTexto;
      g.textBaseline = 'middle';
      g.textAlign = campo.align;
      const cx = campo.align === 'left' ? x : campo.align === 'right' ? x + w : x + w / 2;
      g.fillText(conteudo, cx, y + h / 2);
    });

    return canvas;
  }

  /** PNG em base64 puro (sem o prefixo data:), que é o formato aceito pela Evolution. */
  renderizarBase64(img: HTMLImageElement, campos: CampoArte[], ctx: ContextoArte): string {
    const canvas = this.renderizar(img, campos, ctx);
    const dataUrl = canvas.toDataURL('image/png');
    return dataUrl.split(',')[1] || dataUrl;
  }

  // ------------------------------------------------------- amostra de cor

  /**
   * Descobre sozinho a cor do fundo e a cor do texto de um trecho da arte.
   * Fundo: cor predominante na moldura em volta da caixa (fora do texto).
   * Texto: dentro da caixa, a cor predominante entre as que destoam do fundo.
   * Evita que o operador tenha que acertar o valor exato do amarelo no conta-gotas.
   */
  amostrarCores(img: HTMLImageElement, caixa: CaixaArte): { corTexto: string; corFundo: string } {
    const W = img.naturalWidth || img.width;
    const H = img.naturalHeight || img.height;
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const g = this.contexto2d(canvas);
    g.drawImage(img, 0, 0, W, H);

    const x0 = Math.max(0, Math.round(caixa.x * W));
    const y0 = Math.max(0, Math.round(caixa.y * H));
    const w = Math.max(1, Math.min(W - x0, Math.round(caixa.w * W)));
    const h = Math.max(1, Math.min(H - y0, Math.round(caixa.h * H)));

    const margem = Math.max(2, Math.round(h * 0.15));
    const mx0 = Math.max(0, x0 - margem);
    const my0 = Math.max(0, y0 - margem);
    const mw = Math.min(W - mx0, w + margem * 2);
    const mh = Math.min(H - my0, h + margem * 2);

    const externo = g.getImageData(mx0, my0, mw, mh);
    const interno = g.getImageData(x0, y0, w, h);

    // fundo = moldura externa, ignorando o miolo (onde está o texto)
    const contagemFundo = new Map<string, number>();
    for (let py = 0; py < mh; py++) {
      for (let px = 0; px < mw; px++) {
        const dentroDoMiolo =
          px >= (x0 - mx0) && px < (x0 - mx0) + w &&
          py >= (y0 - my0) && py < (y0 - my0) + h;
        if (dentroDoMiolo) {
          continue;
        }
        this.contar(contagemFundo, externo.data, (py * mw + px) * 4);
      }
    }
    const corFundo = this.predominante(contagemFundo) || '#000000';
    const rgbFundo = this.hexParaRgb(corFundo);

    // texto = dentro da caixa, o que mais se afasta do fundo
    const contagemTexto = new Map<string, number>();
    for (let i = 0; i < interno.data.length; i += 4) {
      const dr = interno.data[i] - rgbFundo[0];
      const dg = interno.data[i + 1] - rgbFundo[1];
      const db = interno.data[i + 2] - rgbFundo[2];
      if (Math.sqrt(dr * dr + dg * dg + db * db) > 80) {
        this.contar(contagemTexto, interno.data, i);
      }
    }
    const corTexto = this.predominante(contagemTexto) || this.contraste(corFundo);

    return { corTexto, corFundo };
  }

  private contexto2d(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
    const g = canvas.getContext('2d');
    if (!g) {
      throw new Error('Canvas 2D indisponível neste navegador.');
    }
    return g;
  }

  /** Agrupa tons quase iguais (antialias) somando em faixas de 8. */
  private contar(mapa: Map<string, number>, data: Uint8ClampedArray, i: number): void {
    if (data[i + 3] < 200) {
      return;
    }
    const chave = [
      Math.round(data[i] / 8) * 8,
      Math.round(data[i + 1] / 8) * 8,
      Math.round(data[i + 2] / 8) * 8
    ].join(',');
    mapa.set(chave, (mapa.get(chave) || 0) + 1);
  }

  private predominante(mapa: Map<string, number>): string | null {
    let melhor: string | null = null;
    let maior = 0;
    mapa.forEach((qtd, chave) => {
      if (qtd > maior) {
        maior = qtd;
        melhor = chave;
      }
    });
    if (!melhor) {
      return null;
    }
    const [r, g, b] = melhor.split(',').map(Number);
    return this.rgbParaHex(r, g, b);
  }

  private rgbParaHex(r: number, g: number, b: number): string {
    const hex = (v: number): string => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0');
    return `#${hex(r)}${hex(g)}${hex(b)}`.toUpperCase();
  }

  private hexParaRgb(hex: string): [number, number, number] {
    const limpo = (hex || '#000000').replace('#', '');
    return [
      parseInt(limpo.substring(0, 2), 16) || 0,
      parseInt(limpo.substring(2, 4), 16) || 0,
      parseInt(limpo.substring(4, 6), 16) || 0
    ];
  }

  private contraste(hexFundo: string): string {
    const [r, g, b] = this.hexParaRgb(hexFundo);
    return (r * 299 + g * 587 + b * 114) / 1000 > 128 ? '#000000' : '#FFFFFF';
  }
}
